/**
 * Product Grid View Toggle
 * Handles switching between grid and list views with localStorage persistence
 */

class ProductGridViewToggle {
  constructor(container) {
    this.container = container;
    this.grid = container.querySelector("#product-grid");
    this.buttons = container.querySelectorAll(".view-toggle__btn");
    this.header = container.querySelector(".product-list__header");
    this.storageKey = "product-grid-view";

    if (!this.grid || this.buttons.length === 0) return;

    this.init();
  }

  destroy() {
    // Clean up event listeners
    if (this.buttons) {
      this.buttons.forEach((btn) => {
        if (btn._viewToggleHandler) {
          btn.removeEventListener("click", btn._viewToggleHandler);
          delete btn._viewToggleHandler;
        }
      });
    }
  }

  init() {
    // Get saved view or use default from data attribute
    const defaultView = this.grid.dataset.defaultView || "grid";
    const savedView = localStorage.getItem(this.storageKey) || defaultView;

    // Set initial view
    this.setView(savedView, false);

    // Add click handlers with cleanup support
    this.buttons.forEach((btn) => {
      const handler = () => {
        const view = btn.dataset.view;
        this.setView(view, true);
      };

      btn.addEventListener("click", handler);
      btn._viewToggleHandler = handler; // Store for cleanup
    });
  }

  setView(view, save = true) {
    // Update container classes for global styling hooks
    this.container.classList.remove("is-view-grid", "is-view-list");
    this.container.classList.add(`is-view-${view}`);

    // Update grid classes
    this.grid.classList.remove("product-grid--grid", "product-grid--list");
    this.grid.classList.add(`product-grid--${view}`);

    // Update button states
    this.buttons.forEach((btn) => {
      const isActive = btn.dataset.view === view;
      btn.classList.toggle("active", isActive);
      btn.setAttribute("aria-pressed", isActive);
    });

    // Show/hide column headers
    if (this.header) {
      if (view === "list") {
        this.header.style.display = "grid";
      } else {
        this.header.style.display = "none";
      }
    }

    // Save preference
    if (save) {
      localStorage.setItem(this.storageKey, view);
    }
  }
}

// Global reference to current toggle instance
window._currentViewToggleInstance = null;

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initViewToggle);
} else {
  initViewToggle();
}

function initViewToggle() {
  const container = document.querySelector(".collection");
  if (container) {
    // Clean up previous instance
    if (
      window._currentViewToggleInstance &&
      window._currentViewToggleInstance.destroy
    ) {
      window._currentViewToggleInstance.destroy();
    }

    window._currentViewToggleInstance = new ProductGridViewToggle(container);
  }
}

// Re-initialize after AJAX updates (facet filtering, pagination, etc.)
document.addEventListener("shopify:section:load", initViewToggle);

// Hook into Shopify's native facet rendering system
function hookIntoFacetSystem() {
  // Wait for FacetFiltersForm to be available
  if (
    typeof FacetFiltersForm === "undefined" ||
    !FacetFiltersForm.renderProductGridContainer
  ) {
    setTimeout(hookIntoFacetSystem, 100);
    return;
  }

  // Store the original method
  if (!FacetFiltersForm._originalRenderProductGridContainer) {
    FacetFiltersForm._originalRenderProductGridContainer =
      FacetFiltersForm.renderProductGridContainer;

    // Override with our hooked version
    FacetFiltersForm.renderProductGridContainer = function (html) {
      // Call the original method first
      const result = FacetFiltersForm._originalRenderProductGridContainer.call(
        this,
        html,
      );

      setTimeout(() => {
        initViewToggle();
      }, 100);

      return result;
    };
  }
}

// Monitor for AJAX content changes as backup
let contentObserver = null;

function setupContentMonitoring() {
  if (contentObserver) {
    contentObserver.disconnect();
  }

  const productGridContainer = document.getElementById("ProductGridContainer");
  if (!productGridContainer) {
    setTimeout(setupContentMonitoring, 500);
    return;
  }

  contentObserver = new MutationObserver((mutations) => {
    let shouldReinitialize = false;

    mutations.forEach((mutation) => {
      if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
        // Check if the collection container exists and has view toggle buttons
        const container = document.querySelector(".collection");
        const buttons = container?.querySelectorAll(".view-toggle__btn");

        if (buttons && buttons.length > 0) {
          // Check if existing instance is still working
          let needsReset = false;

          // If we don't have an instance reference, we need to initialize
          if (!window._currentViewToggleInstance) {
            needsReset = true;
          } else {
            // Check if buttons have proper event handlers
            const hasWorkingButtons = Array.from(buttons).some(
              (btn) =>
                btn._viewToggleHandler && btn.classList.contains("active"),
            );
            if (!hasWorkingButtons) {
              needsReset = true;
            }
          }

          if (needsReset) {
            shouldReinitialize = true;
          }
        }
      }
    });

    if (shouldReinitialize) {
      setTimeout(initViewToggle, 150);
    }
  });

  contentObserver.observe(productGridContainer, {
    childList: true,
    subtree: true,
  });
}

// Hook into the facet system
setTimeout(hookIntoFacetSystem, 200);

// Set up content monitoring as backup
setTimeout(setupContentMonitoring, 500);

// Also set up after section loads
document.addEventListener("shopify:section:load", () => {
  setTimeout(() => {
    hookIntoFacetSystem();
    setupContentMonitoring();
  }, 100);
});

// Periodic check as final fallback
setInterval(() => {
  const container = document.querySelector(".collection");
  const buttons = container?.querySelectorAll(".view-toggle__btn");

  if (buttons && buttons.length > 0) {
    // Check if any button has an event handler
    const hasWorkingHandlers = Array.from(buttons).some(
      (btn) => btn._viewToggleHandler,
    );

    if (!hasWorkingHandlers || !window._currentViewToggleInstance) {
      initViewToggle();
    }
  }
}, 3000);

// Make initViewToggle globally accessible
window.initViewToggle = initViewToggle;
